import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SolicitudesService } from 'src/app/services/integracion-pos/solicitudes.service';

/**
 * El detalle de una solicitud de crédito — PÁGINA con ruta propia
 * (`/console/solicitudes-credito/:code`), nunca un modal.
 *
 * Contrato §4.2: `~/Documents/multicomercio/competencia/60-modulo-solicitudes-contrato.md`.
 * Rediseño: documentos 63 (arquitectura, §4.2), 64 (el carril del cobro, §3.1) y 66 (la
 * propuesta aprobada), todos del 2026-09-20.
 *
 * 🔴 LA FRASE DEL OFICIO 0: quien abre esto tiene que poder contestarle a alguien que reclama
 * dónde quedó esta solicitud, por qué, y a quién le corresponde. Antes eran 29 tarjetas
 * apiladas y esa frase no se podía escribir. Ahora son CUATRO BLOQUES, sin pestañas:
 *   1 · Cabecera (sin rótulo) — quién, cuánto, y la frase de qué pasó.
 *   2 · Historial — la línea de tiempo, pieza central, con el carril del cobro adentro.
 *   3 · Plan de pago — antes no existía.
 *   4 · Crédito — el que nació, o por qué no.
 * La regla que lo ordena (documento 63 §4.2): **lo que tiene fecha vive en la línea de tiempo;
 * lo que no tiene fecha vive en la ficha.** Por eso los intentos de cobro y la anulación —los
 * dos tienen `at`— ya no son tarjetas propias: el carril y la tabla de intentos viven DENTRO del
 * bloque Historial, y la anulación se cuenta en su propio hito `ANULADA`.
 *
 * 🔴 HALLAZGO DE ESTA VUELTA, no documentado en el contrato: el componente anterior leía
 * `this.solicitud?.eventos` para el historial. El backend real
 * (`modules/integracion-pos/casos/listado-solicitudes.js:aDetalle`, línea `historial: (...)`,
 * confirmado contra `http/rutas.js:63` — `res.status(r.http).json(r.cuerpo)`, sin ninguna capa
 * que renombre la clave) manda el campo con el nombre **`historial`**, nunca `eventos`. El
 * contrato (documento 60 §4.2) lo llama `eventos` en su ejemplo, pero eso no es lo que el
 * servidor manda hoy. Con el nombre viejo, `this.solicitud?.eventos` era `undefined` SIEMPRE:
 * la línea de tiempo —descrita en los comentarios de este mismo archivo como «la pieza
 * central»— nunca tuvo con qué dibujarse, en ninguna solicitud, y no daba ningún error. Se
 * corrige acá, leyendo `historial` tal cual lo manda el servidor.
 *
 * 🔴 §4.2 FIJA LA FORMA EXACTA, medida contra el código del backend: `monto.usd`,
 * `initial_payment.amount_usd`, `initial_payment.merchant_invoice`, `expires_at`, `rastro.etapa`,
 * `cobro_attempts`, `annulment`, `plan.installments[]`, `credito_real` (`{numero, activo,
 * payment_status:{code,name}}` — medido en `infra/repos/solicitudes.js:estadoDelCredito`, no el
 * `identifier` del ejemplo ilustrativo del contrato) e `historial` (con `tipo`/`en`/`actor`/
 * `datos` en castellano). Acá se lee ESE nombre, no uno adivinado: tantear varios (`a?.b ?? a?.c`)
 * es lo que escondió el defecto de la certificación del 2026-09-20 — no falla nunca, y por eso
 * nunca avisa que el nombre está mal.
 */
@Component({
  selector: 'app-solicitudes-credito-detalle',
  templateUrl: './solicitudes-credito-detalle.component.html',
  styleUrls: ['./solicitudes-credito-detalle.component.css']
})
export class SolicitudesCreditoDetalleComponent implements OnInit {

  codigo = '';
  solicitud: any = null;
  cargando = true;
  noEncontrada = false;
  error = '';
  /** `false` sólo cuando el propio servidor dice `retryable:false` (dominio/codigos.js).
   *  Sin ese dato —p.ej. el `{msg}` del middleware de JWT— no hay manera de afirmar que
   *  reintentar no sirve, así que por omisión se ofrece igual. */
  errorReintentable = true;

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _solicitudes: SolicitudesService
  ) { }

  ngOnInit(): void {
    this.ruta.paramMap.subscribe(p => {
      this.codigo = p.get('code') || '';
      this.cargar();
    });
  }

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.noEncontrada = false;
    this.error = '';
    this.errorReintentable = true;
    try {
      const rep = await this._solicitudes.getSolicitud(this.codigo);
      // El endpoint puede devolver la solicitud pelada o envuelta ({solicitud:{...}}).
      this.solicitud = rep?.solicitud || rep?.item || rep || null;
      this.noEncontrada = !this.solicitud;
    } catch (e: any) {
      this.solicitud = null;
      if (e?.status === 404) this.noEncontrada = true;
      else {
        this.error = this.mensajeError(e);
        this.errorReintentable = this.esReintentable(e);
      }
    } finally {
      this.cargando = false;
    }
  };

  /**
   * 🔴 El servidor manda DOS formas de error, y ninguna es la que se leía antes
   * (`e?.error?.msg` a secas): la del propio módulo, `{error:{code,message,retryable}}`
   * (`dominio/codigos.js:error`), y la del middleware de JWT/cron, que manda `{msg}` sin
   * envolver (`middlewares/validate-cron-or-admin-jwt.js`). Ninguna de las dos coincidía con lo
   * que se leía, así que siempre caía en el mensaje genérico.
   */
  private mensajeError = (e: any): string =>
    e?.error?.error?.message || e?.error?.msg || 'No se pudo traer esta solicitud. Intenta de nuevo en un momento.';

  /** Sólo la forma del módulo trae `retryable`. Cuando dice `false` en firme —como
   *  `NO_ES_CREDITOTAL`, un 403 que jamás se arregla reintentando— no se ofrece el botón. */
  private esReintentable = (e: any): boolean => e?.error?.error?.retryable !== false;

  volver = (): void => { this.router.navigate(['/console/solicitudes-credito']); };

  // ══ 1 · LA CABECERA — quién, cuánto, y qué pasó ═══════════════════════════

  // 2026-09-25: la cédula con sus puntos y el nombre del cliente, como en el portal.
  get cedula(): string { return this.fmtCedula(this.solicitud?.cliente?.cedula); }
  get clienteNombre(): string | null {
    const n = String(this.solicitud?.cliente?.nombre || '').trim();
    if (!n) return null;
    if (!(n === n.toLowerCase() || n === n.toUpperCase())) return n;
    return n.toLowerCase().replace(/(^|\s)(\p{L})/gu, (_: string, a: string, b: string) => a + b.toUpperCase());
  }
  /** La tienda por su nombre, sin repetir el comercio que ya va al lado. Sin nombre, por su código. */
  get tiendaTexto(): string {
    const n = String(this.solicitud?.tienda_nombre || '').trim();
    if (!n) return `Tienda ${this.tienda}`;
    const c = String(this.comercio?.name || '').trim();
    return c && n.toLowerCase().startsWith(`${c.toLowerCase()} · `) ? n.slice(c.length + 3) : n;
  }
  /** «V19163767» → «V-19.163.767». Lo que no parece cédula, tal cual. */
  fmtCedula = (v: any): string => {
    const m = String(v ?? '').trim().toUpperCase().match(/^([VEJPG])-?(\d{5,9})$/);
    if (!m) return v ? String(v) : '—';
    return `${m[1]}-${Number(m[2]).toLocaleString('es-VE')}`;
  };
  get comercio(): any { return this.solicitud?.comercio || null; }
  get tienda(): any { return this.solicitud?.tienda ?? null; }
  get caja(): any { return this.solicitud?.caja ?? null; }
  get cajeroCedula(): string { return this.fmtCedula(this.solicitud?.cajero_cedula); }
  get montoUsd(): number | null { return this.solicitud?.monto?.usd ?? null; }
  get inicialUsd(): number | null { return this.solicitud?.initial_payment?.amount_usd ?? null; }
  /** 🔴 2026-09-25: LO QUE LA CAJA TIENE QUE COBRAR es la inicial MÁS el cargo por uso y los extras
   *  (P-267, `cobro_inicial.total_usd` del núcleo). Contra la inicial sola, un cobro exacto de USD 6,00
   *  (5 + 1 de uso) se leía «sobran USD 1,00», y la fila del intento decía «Exacto». */
  get totalACobrarUsd(): number | null {
    const t = this.solicitud?.cobro_inicial?.total_usd;
    return (t !== null && t !== undefined) ? Number(t) : this.inicialUsd;
  }
  /** «inicial 5,00 + cargo por uso 1,00», para decir de qué se compone lo cobrado. */
  get desgloseCobro(): string {
    const c = this.solicitud?.cobro_inicial;
    if (!c) return '';
    const partes = [`inicial ${this.formatoNumero(c.inicial_usd)}`];
    if (c.cargo_por_uso?.amount_usd) partes.push(`cargo por uso ${this.formatoNumero(c.cargo_por_uso.amount_usd)}`);
    if (c.extras_total_usd) partes.push(`extras ${this.formatoNumero(c.extras_total_usd)}`);
    return partes.length > 1 ? partes.join(' + ') : '';
  }
  get factura(): string { return this.solicitud?.initial_payment?.merchant_invoice || '—'; }
  get vence(): string | null { return this.solicitud?.expires_at || null; }
  get status(): string { return this.solicitud?.status || ''; }
  get etapa(): string { return this.solicitud?.rastro?.etapa || ''; }
  get rastro(): any { return this.solicitud?.rastro || null; }
  get closedReason(): string | null { return this.solicitud?.closed_reason || null; }
  get exchangeRate(): any { return this.solicitud?.exchange_rate || null; }
  get requestType(): string | null { return this.solicitud?.request_type || null; }
  get plan(): any { return this.solicitud?.plan || null; }
  get annulment(): any { return this.solicitud?.annulment || null; }

  /** Sólo mientras sigue viva tiene sentido mostrar un plazo — una vencida ya vencida no
   *  necesita un reloj de arena. Mismo criterio que el listado (`aItem:vence`, §4.1). */
  get vigente(): boolean { return ['EN_ESPERA', 'ACEPTADA', 'EN_PROCESO'].includes(this.status); }

  /** Un cierre queda escrito en `closed_at`; mientras no lo tenga, el último hito real todavía
   *  puede moverse — por eso la línea de tiempo agrega el nodo hueco «Sigue aquí». */
  get esTerminal(): boolean { return !!this.solicitud?.closed_at; }

  /** El número visible del crédito. `null` si la solicitud no llegó a nacer crédito.
   *  ⚠️ Sin enlace a propósito: no hay hoy una pantalla que resuelva `CREDIT-…` por su número
   *  (se buscó en `console/invoice-detail/:code` y `console/cliente/:code`, ninguna sirve). */
  get creditoNumero(): string | null { return this.solicitud?.credito?.numero || null; }

  copiado = false;
  copiarCredito = async (): Promise<void> => {
    if (!this.creditoNumero) return;
    try {
      await navigator.clipboard.writeText(this.creditoNumero);
      this.copiado = true;
      setTimeout(() => { this.copiado = false; }, 1600);
    } catch { /* sin permiso de portapapeles: no rompe nada, sólo no copia */ }
  };

  readonly TEXTO_ESTATUS: any = {
    EN_ESPERA: 'En espera', ACEPTADA: 'Aceptada', CONFIRMADA: 'Confirmada',
    ANULADA: 'Anulada', VENCIDA: 'Vencida', EN_PROCESO: 'En proceso',
    RECHAZADA_POR_EL_CLIENTE: 'Rechazada por el cliente',
    RECHAZADA_EN_VERIFICACION: 'Rechazada en verificación',
  };

  textoEstatus = (status: string): string =>
    this.TEXTO_ESTATUS[status] || (String(status || '').startsWith('RECHAZADA') ? 'Rechazada' : (status || '—'));

  readonly TEXTO_ETAPA: any = {
    CREDITO_INTENTO: 'Creando el crédito', CREDITO_CREADO: 'Crédito creado',
    CREDITO_FALLIDO: 'El crédito no llegó a crearse', COBRO_INTENTO: 'Asentando el cobro',
    COBRO_ASENTADO: 'Cobro asentado', COBRO_INCOMPLETO: 'Cobro incompleto', COBRO_FALLIDO: 'El cobro falló',
  };

  textoEtapa = (etapa: string): string => this.TEXTO_ETAPA[etapa] || (etapa || '—');

  private readonly TEXTO_REQUEST_TYPE: any = {
    POS_COMPRA: 'Compra en comercio', WEB_COMPRA: 'Compra por web',
    MERCHANT: 'Integración del comercio', LINK_PAGO: 'Link de pago',
  };

  /** «Tipo de solicitud», no «tipo de conexión» — decisión de Juan, documento 66 §7.2: el
   *  backend dice `request_type` y ésa es la palabra que gana. */
  textoRequestType = (tipo: string | null | undefined): string | null =>
    tipo ? (this.TEXTO_REQUEST_TYPE[tipo] || this.etiquetar(tipo)) : null;

  private readonly TEXTO_CLOSED_REASON: any = {
    CLIENTE_INACTIVO: 'El cliente está inactivo o bloqueado',
    CUPO_INSUFICIENTE: 'El cupo no alcanzaba para el monto pedido',
    CUPO_INACTIVO: 'El cupo quedó inactivo por mora',
    MONTO_FUERA_DE_PLAN: 'El monto no calzaba en ningún plan',
    SIN_PLAN_APLICABLE: 'No había un plan que aplicara',
    ANULADA_POR_EL_COMERCIO: 'El comercio la anuló, sin motivo registrado',
  };

  /**
   * `closed_reason` es lo único que dice POR QUÉ se cayó una solicitud — 18 de 83 son
   * `RECHAZADA_EN_VERIFICACION` y hoy este dato no se muestra en ninguna parte (documento 66 §2).
   * Los códigos conocidos (`infra/nucleo/persona.js:codigoDeCliente`, `infra/nucleo/planes.js`)
   * se traducen; los que ya vienen en castellano llano desde el dominio
   * (`casos/vencer-solicitudes.js:MOTIVO`, `casos/decidir-solicitud.js:'rechazo del cliente'`)
   * sólo se capitalizan. Lo que no se reconoce se formatea —sin guiones bajos—, nunca crudo.
   */
  textoClosedReason = (reason: string | null | undefined): string | null =>
    reason ? (this.TEXTO_CLOSED_REASON[reason] || this.etiquetar(reason)) : null;

  formatoMonto = (n: any): string =>
    (n === null || n === undefined) ? '—' : `USD ${Number(n).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  formatoNumero = (n: any): string =>
    (n === null || n === undefined) ? '—' : Number(n).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  /** `dd/mm/aaaa hh:mm`, para usar DENTRO de una frase o un `<title>` — donde el pipe `date` de
   *  la plantilla no llega (getters de TypeScript). */
  private formatoFechaHora = (iso: any): string => {
    if (!iso) return 'sin fecha';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'sin fecha';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
  };

  /**
   * 🔴 LA PASTILLA DE NEGOCIO — decisión de Juan, 2026-09-20 (documento 66 §7.1): estatus y
   * etapa se funden en una sola pastilla, igual que ya hace el listado (documento 63 §3.5),
   * porque el caso caro (`CONFIRMADA` + `COBRO_INCOMPLETO`) se ve idéntico al sano si se
   * muestran dos pastillas sueltas. Se llama **«Trabada»**. El ejemplo de cabecera del
   * documento 63 §4.2 es literal: `SOL-MT-000123 [ Trabada ]`, una sola pastilla.
   */
  get estadoNegocio(): { texto: string; clase: string } {
    const s = this.status;
    const e = this.etapa;
    if (s === 'CONFIRMADA') {
      if (e === 'COBRO_INCOMPLETO' || e === 'COBRO_FALLIDO' || e === 'CREDITO_FALLIDO') {
        return { texto: 'Trabada', clase: 'kb-badge-alerta' };
      }
      if (e === 'COBRO_ASENTADO') return { texto: 'Confirmada', clase: 'kb-badge-exito' };
      // Confirmada sin rastro escrito: sí, no, y todavía no se sabe — no se pinta verde.
      return { texto: 'Confirmada', clase: 'kb-badge-neutro' };
    }
    if (s === 'ACEPTADA') return { texto: 'Aceptada', clase: 'kb-badge-alerta' };
    if (s === 'VENCIDA') return { texto: 'Vencida', clase: 'kb-badge-alerta' };
    if (s === 'ANULADA') return { texto: 'Anulada', clase: 'kb-badge-peligro' };
    if (String(s).startsWith('RECHAZADA')) return { texto: this.textoEstatus(s), clase: 'kb-badge-peligro' };
    return { texto: this.textoEstatus(s), clase: 'kb-badge-neutro' };
  }

  /**
   * 🔴 LA FRASE QUE CONTESTA EL RECLAMO, antes que cualquier tarjeta (documento 63 §1.2/§4.2):
   * «Se cobró $100 el 20/09 a las 14:35 y el crédito CREDIT-… no quedó firme. Falta imputar el
   * pago a la cuota inicial.» Una por estado, con el dato exacto que cada reclamo necesita —
   * `closed_reason` para una rechazada, quién autorizó para una anulada, la escritura que falló
   * para una trabada.
   */
  get fraseQuePaso(): string {
    const s = this.status;
    const e = this.etapa;
    const numero = this.creditoNumero;

    if (s === 'CONFIRMADA') {
      if (e === 'COBRO_INCOMPLETO' || e === 'COBRO_FALLIDO') {
        let f = `Se cobró ${this.formatoMonto(this.inicialUsd ?? this.montoUsd)}`;
        if (this.rastro?.at) f += ` el ${this.formatoFechaHora(this.rastro.at)}`;
        f += numero ? ` y el crédito ${numero} no quedó firme.` : ' y el crédito no quedó firme.';
        const pasos = this.rastro?.detalle?.pasos;
        const claveFalta = pasos ? Object.keys(pasos).find(k => pasos[k] === false) : null;
        if (claveFalta && this.TEXTO_FALTA_PASO[claveFalta]) {
          const texto = this.TEXTO_FALTA_PASO[claveFalta];
          f += ` ${texto.charAt(0).toUpperCase()}${texto.slice(1)}.`;
        }
        return f;
      }
      if (e === 'CREDITO_FALLIDO') return 'Se cobró la inicial, pero el crédito no llegó a crearse.';
      if (e === 'COBRO_ASENTADO') {
        const d = this.desgloseCobro;
        return `Se cobró ${this.formatoMonto(this.totalACobrarUsd)}${d ? ` (${d})` : ''} y el crédito ${numero || 'asociado'} nació firme.`;
      }
      return 'La caja confirmó el cobro. Todavía no hay rastro de cómo quedó — no se afirma que esté bien.';
    }
    if (s === 'ACEPTADA') {
      return `El cliente firmó. Falta que la caja cobre la inicial${this.vence ? ' — vence ' + this.formatoFechaHora(this.vence) : ''}.`;
    }
    if (s === 'EN_ESPERA' || s === 'EN_PROCESO') return 'Está esperando que el cliente conteste.';
    if (s === 'VENCIDA') {
      const motivo = this.textoClosedReason(this.closedReason);
      return motivo ? `Venció: ${motivo}.` : 'Venció, sin motivo registrado.';
    }
    if (s === 'ANULADA') {
      const a = this.annulment;
      if (!a) return 'Se anuló, sin datos de quién la autorizó.';
      const partes = [
        a.authorized_by ? `la autorizó ${a.authorized_by}` : null,
        a.authorization_code ? `código ${a.authorization_code}` : null,
        a.cashier ? `la ejecutó ${a.cashier}` : null,
      ].filter(Boolean).join(' · ');
      return `Se anuló${partes ? ' — ' + partes : ''}.${a.reason ? ' Motivo: ' + a.reason + '.' : ''}`;
    }
    if (String(s).startsWith('RECHAZADA')) {
      const motivo = this.textoClosedReason(this.closedReason);
      return motivo ? `${motivo}.` : 'La rechazaron, sin motivo registrado.';
    }
    return '';
  }

  // ══ 2 · HISTORIAL — línea de tiempo, la pieza central de esta pantalla ═══════

  /**
   * §4.2: el campo real es `historial` (ver el hallazgo documentado arriba, en el encabezado
   * de la clase) — no `eventos`, que es el nombre del ejemplo del contrato pero no el que manda
   * el servidor.
   *
   * 🔴 Red de seguridad para solicitudes anteriores al rastro de eventos (el mecanismo es nuevo,
   * pedido de Juan el 2026-09-20): si `accepted_at`/`confirmed_at` existen pero ningún hito los
   * cuenta, se sintetiza el hito para no perder la fecha — documento 66 §2 los lista entre los
   * campos que hoy «tampoco aparecen», y la regla de corte (documento 63 §4.2) dice que una
   * fecha vive en la línea de tiempo, no en una segunda copia suelta en la ficha.
   */
  get historial(): any[] {
    const base: any[] = Array.isArray(this.solicitud?.historial) ? [...this.solicitud.historial] : [];
    const tiene = (tipo: string): boolean => base.some(ev => ev?.tipo === tipo);
    if (this.solicitud?.accepted_at && !tiene('ACEPTADA')) {
      base.push({ tipo: 'ACEPTADA', en: this.solicitud.accepted_at, actor: null, datos: null });
    }
    if (this.solicitud?.confirmed_at && !tiene('CONFIRMADA')) {
      base.push({ tipo: 'CONFIRMADA', en: this.solicitud.confirmed_at, actor: null, datos: null });
    }
    return base.sort((a, b) => new Date(a?.en || 0).getTime() - new Date(b?.en || 0).getTime());
  }

  private etiquetar = (texto: string): string => {
    const limpio = String(texto).replace(/_/g, ' ').toLowerCase().trim();
    return limpio.charAt(0).toUpperCase() + limpio.slice(1);
  };

  private readonly TEXTO_EVENTO: any = {
    CREADA: 'Se creó la solicitud', PLAN_RESUELTO: 'Se resolvió el plan de pago',
    AVISADA: 'Se avisó al cliente', PASO_CUMPLIDO: 'El cliente cumplió un paso del flujo',
    PASO_RECHAZADO: 'El cliente rechazó un paso del flujo', ACEPTADA: 'El cliente aceptó los términos y condiciones',
    RECHAZADA: 'El cliente rechazó la solicitud', VENCIDA: 'La solicitud venció sin que el cliente aceptara',
    CANCELADA: 'El comercio canceló la solicitud', CREDITO_INTENTO: 'Se pidió crear el crédito',
    CREDITO_CREADO: 'Nació el crédito', CREDITO_FALLIDO: 'El crédito no llegó a crearse',
    COBRO_INTENTO: 'Se pidió asentar el cobro de la inicial', COBRO_ASENTADO: 'El cobro quedó asentado',
    COBRO_INCOMPLETO: 'El cobro quedó a medias', COBRO_FALLIDO: 'El cobro falló',
    CONFIRMADA: 'La caja confirmó el cobro', ANULADA: 'Se anuló la solicitud', NOTIFICADA: 'Se notificó al cliente',
    RECHAZADA_POR_EL_CLIENTE: 'El cliente rechazó la solicitud', RECHAZADA_EN_VERIFICACION: 'No pasó verificación',
  };

  fechaHito = (h: any): string | null => h?.en || null;

  tituloHito = (h: any): string => {
    const tipo = h?.tipo;
    if (!tipo) return 'Movimiento registrado';
    return this.TEXTO_EVENTO[tipo] || this.etiquetar(String(tipo));
  };

  private readonly TEXTO_ACTOR: any = { COMERCIO: 'El comercio', CLIENTE: 'El cliente', SISTEMA: 'El sistema' };

  actorHito = (h: any): string | null => {
    const kind = h?.actor?.kind;
    if (!kind) return null;
    const texto = this.TEXTO_ACTOR[kind] || this.etiquetar(String(kind));
    // 🔴 2026-09-25: el «ref» crudo decía «El cliente · f829ee7b-9507-…» (su código interno) y
    // «El comercio · 1». Del cliente va su nombre; un número suelto es la caja; una cédula, con puntos.
    const ref = String(h?.actor?.ref ?? '').trim();
    if (!ref) return `Lo hizo ${texto}`;
    if (kind === 'CLIENTE') return this.clienteNombre ? `Lo hizo ${texto} · ${this.clienteNombre}` : `Lo hizo ${texto}`;
    if (/^\d{1,4}$/.test(ref)) return `Lo hizo ${texto} · caja ${ref}`;
    if (/^[VEJPG]-?\d{5,9}$/i.test(ref)) return `Lo hizo ${texto} · ${this.fmtCedula(ref)}`;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(ref) || /^[0-9a-f]{24}$/i.test(ref)) return `Lo hizo ${texto}`;
    return `Lo hizo ${texto} · ${ref}`;
  };

  private formatoValorDato = (v: any): string => {
    if (typeof v === 'boolean') return v ? 'Sí' : 'No';
    if (v && typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  /** `datos` del evento (p.ej. `{motivo}` en una rechazada). `pasos` se muestra aparte, como
   *  checklist (`pasosHito`), no como texto. */
  detalleHito = (h: any): string | null => {
    const datos = h?.datos;
    if (!datos || typeof datos !== 'object') return null;
    const entradas = Object.keys(datos)
      .filter(k => k !== 'pasos' && datos[k] !== null && datos[k] !== undefined && datos[k] !== '');
    if (!entradas.length) return null;
    // 2026-09-25: las claves en palabras («Credito: … · Ya existia: No · Inicial usd: 5» eran los
    // nombres de campo del núcleo). Los montos, con su moneda.
    const ROTULO: any = { credito: 'Crédito', ya_existia: 'Ya existía', inicial_usd: 'Inicial', cargo_usd: 'Cargo por uso',
      extras_usd: 'Extras', factura: 'Factura', plan: 'Plan', plan_code: 'Plan', motivo: 'Motivo', orden: 'Orden' };
    return entradas.map(k => {
      const v = datos[k];
      const valor = /_usd$/.test(k) && typeof v === 'number' ? this.formatoMonto(v) : this.formatoValorDato(v);
      return `${ROTULO[k] || this.etiquetar(k)}: ${valor}`;
    }).join(' · ');
  };

  /**
   * La anulación (`annulment`) vive fuera de `historial[]`, pero pertenece al hito `ANULADA`
   * (documento 63 §4.2: «la anulación con su autorizante y su motivo en el hito ANULADA»). El
   * `datos` del propio evento sólo trae `{autorizado_por, motivo}`
   * (`infra/repos/solicitudes.js:anular`); el código de autorización y el cajero que la
   * ejecutó sólo están en el objeto top-level — se completan desde ahí para no mostrar un
   * renglón a medias, y ya no hace falta una tarjeta «Anulación» aparte.
   */
  detalleParaHito = (h: any): string | null => {
    if (h?.tipo === 'ANULADA' && this.annulment) {
      const a = this.annulment;
      const partes = [
        a.authorized_by ? `autorizada por ${a.authorized_by}` : null,
        a.authorization_code ? `código ${a.authorization_code}` : null,
        a.cashier ? `la ejecutó ${a.cashier}` : null,
      ].filter(Boolean);
      const unida = partes.join(' · ');
      let texto: string | null = unida ? (unida.charAt(0).toUpperCase() + unida.slice(1)) : null;
      if (a.reason) texto = (texto ? texto + ' — ' : '') + a.reason;
      return texto || this.detalleHito(h);
    }
    return this.detalleHito(h);
  };

  private readonly PASOS_DEL_COBRO: [string, string][] = [
    ['credito_firme', 'El crédito quedó firme'],
    ['pago_imputado', 'El pago se imputó a la cuota inicial'],
    ['cuota_saldada', 'La cuota inicial quedó saldada'],
    ['cupo_descontado', 'Se descontó del cupo del cliente'],
  ];

  private readonly TEXTO_FALTA_PASO: any = {
    credito_firme: 'el crédito no quedó firme',
    pago_imputado: 'falta imputar el pago a la cuota inicial',
    cuota_saldada: 'la cuota inicial no quedó saldada',
    cupo_descontado: 'no se pudo comprobar si se descontó el cupo',
  };

  /** Las cuatro escrituras que verifica un cobro (`infra/nucleo/rastro.js:verificarCobro`).
   *  `null` es «no se pudo comprobar», y se dice así — no es lo mismo que «no se hizo». */
  private pasosDelCobro = (datos: any): { texto: string; ok: boolean | null }[] | null => {
    const pasos = datos?.pasos;
    if (!pasos || typeof pasos !== 'object') return null;
    return this.PASOS_DEL_COBRO.map(([clave, texto]) => ({
      texto, ok: pasos[clave] === undefined ? null : pasos[clave],
    }));
  };

  /**
   * 🔴 UNA SOLA VEZ (documento 66 §2, ⚠️): antes se pintaban dos veces — adentro del hito y otra
   * vez en la tarjeta «Rastro del crédito», que ya no existe. `pasos` viaja directo en el
   * `datos` del evento COBRO_* (`casos/confirmar-cobro.js:161`); si por algo faltara ahí, se
   * cae a `rastro.detalle.pasos` —la misma fuente— sólo para el hito cuyo tipo coincide con la
   * etapa actual del rastro, así que nunca se duplica en más de un hito.
   */
  pasosHito = (h: any): { texto: string; ok: boolean | null }[] | null =>
    this.pasosDelCobro(h?.datos) ?? (h?.tipo && h.tipo === this.rastro?.etapa ? this.pasosDelCobro(this.rastro?.detalle) : null);

  colorHito = (h: any): string => {
    const tipo = String(h?.tipo || '').toUpperCase();
    if (/ANUL|RECHAZ|VENC|FALLID/.test(tipo)) return 'bg-destructive';
    if (/INCOMPLET/.test(tipo)) return 'kb-punto-alerta';
    if (/CONFIRM|ACEPT|ASENTAD|CREAD/.test(tipo)) return 'kb-punto-exito';
    return 'kb-punto-apagado';
  };

  /** El hueco entre un hito y el siguiente, en palabras — documento 64 §3.3: «una lista de
   *  fechas obliga a restar de cabeza». `i` es el índice dentro de `historial`. */
  huecoAntes = (i: number): string | null => {
    if (i <= 0) return null;
    const h = this.historial;
    const antes = this.fechaHito(h[i - 1]);
    const actual = this.fechaHito(h[i]);
    if (!antes || !actual) return null;
    const ms = new Date(actual).getTime() - new Date(antes).getTime();
    if (isNaN(ms)) return null;
    return `+ ${this.formatoDuracion(ms)}`;
  };

  /** Si la solicitud sigue viva, el último hito no es el final: se agrega un nodo hueco
   *  «Sigue aquí» con cuánto lleva ahí — documento 64 §3.3. */
  get huecoFinal(): string | null {
    const h = this.historial;
    if (this.esTerminal || !h.length) return null;
    const ultima = this.fechaHito(h[h.length - 1]);
    if (!ultima) return null;
    const ms = Date.now() - new Date(ultima).getTime();
    if (isNaN(ms) || ms < 0) return null;
    return `hace ${this.formatoDuracion(ms)}`;
  }

  private formatoDuracion = (ms: number): string => {
    const total = Math.max(ms, 0);
    const minutos = Math.floor(total / 60000);
    if (minutos < 1) return 'menos de 1 min';
    if (minutos < 60) return `${minutos} min`;
    const horas = Math.floor(minutos / 60);
    const minRestantes = minutos % 60;
    if (horas < 24) return minRestantes ? `${horas} h ${minRestantes} min` : `${horas} h`;
    const dias = Math.floor(horas / 24);
    return `${dias} día${dias === 1 ? '' : 's'}`;
  };

  // ── El carril del cobro (§4.2 `cobro_attempts`, documento 64 §3.1) ─────────

  /** Cada intento del cajero al cobrar: de menos, de más o exacto. */
  get cobroAttempts(): any[] {
    const arr = this.solicitud?.cobro_attempts;
    return Array.isArray(arr) ? arr : [];
  }

  /**
   * 🔴 TRES ESTADOS, NO DOS: hubo intentos · no hubo ninguno · **no sabemos**. `cobroAttempts`
   * colapsa `undefined` en `[]`, así que hace falta esta bandera aparte para no confundir «el
   * cajero nunca intentó cobrar» con «el servidor no mandó ese dato» — el defecto más caro que
   * este producto tuvo, y apareció dos veces.
   */
  get sabemosDeIntentos(): boolean {
    return Array.isArray(this.solicitud?.cobro_attempts);
  }

  private readonly TEXTO_RESULTADO_COBRO: any = { de_menos: 'De menos', de_mas: 'De más', exacto: 'Exacto' };

  textoResultadoCobro = (r: string): string => this.TEXTO_RESULTADO_COBRO[r] || r || '—';

  claseResultadoCobro = (r: string): string => {
    if (r === 'exacto') return 'kb-badge-exito';
    if (r === 'de_menos') return 'kb-badge-peligro';
    if (r === 'de_mas') return 'kb-badge-alerta';
    return 'kb-badge-neutro';
  };

  /**
   * 🔴 EL ÚNICO DIBUJO DE ESTA PANTALLA (documento 64 §3.1). Longitud contra una marca de
   * referencia: el objetivo es `initial_payment.amount_usd`, y si viene nulo,
   * `plan.down_payment_amount` — nunca el máximo de la serie. `viewBox="0 0 600 16"`: el
   * objetivo cae siempre en `x=500`, y los 100 que sobran son el espacio del excedente si se
   * cobró de más. Devuelve un objeto plano para que la plantilla sólo dibuje, sin decidir nada.
   */
  get carril(): any {
    if (!this.sabemosDeIntentos) return { estado: 'sin_dato' };

    const intentos = [...this.cobroAttempts].sort(
      (a, b) => new Date(a?.at || 0).getTime() - new Date(b?.at || 0).getTime());

    if (!intentos.length) {
      return {
        estado: 'sin_intentos',
        mensaje: this.status === 'CONFIRMADA'
          ? 'Se cobró de una sola vez, sin intentos parciales.'
          : 'Todavía no se intentó cobrar.',
      };
    }

    const cobrado = intentos.reduce((acc, a) => acc + (Number(a?.amount_usd) || 0), 0);
    const objetivo = this.totalACobrarUsd ?? this.plan?.down_payment_amount ?? null;

    if (!objetivo || objetivo <= 0) {
      return { estado: 'sin_objetivo', cobrado, intentos: intentos.length };
    }

    const ANCHO_OBJETIVO = 500;
    const ANCHO_TOTAL = 600;
    const GAP = 2;
    const escala = ANCHO_OBJETIVO / objetivo;
    let cursor = 0;
    const segmentos = intentos.map((a, i) => {
      const monto = Number(a?.amount_usd) || 0;
      const ancho = Math.max(monto * escala, 0);
      const x = cursor;
      cursor += ancho + GAP;
      return {
        x, ancho,
        titulo: `Intento ${i + 1} · ${this.formatoMonto(monto)} · ${this.formatoFechaHora(a?.at)} · ${a?.cashier || 'cajero sin registrar'}`,
      };
    });
    const anchoTotal = Math.max(cursor - GAP, 0);
    const sobra = cobrado > objetivo;
    const diff = Math.abs(objetivo - cobrado);
    const excedente = sobra
      ? { x: ANCHO_OBJETIVO, ancho: Math.max(Math.min(anchoTotal, ANCHO_TOTAL) - ANCHO_OBJETIVO, 0) }
      : null;

    return {
      estado: 'con_datos', objetivo, cobrado, sobra, diff, segmentos, excedente,
      ariaLabel: `Cobrado ${this.formatoMonto(cobrado)} de los ${this.formatoMonto(objetivo)} del objetivo. `
        + (sobra ? `Sobran ${this.formatoMonto(diff)}.` : `Faltan ${this.formatoMonto(diff)}.`)
        + ` ${intentos.length} intento${intentos.length === 1 ? '' : 's'}.`,
    };
  }

  // ══ 3 · PLAN DE PAGO — no existía (documento 64 §3.2) ═══════════════════════

  /** Cada cuota con su estado, DERIVADO comparando `scheduled_date` con hoy — no pide endpoint
   *  nuevo (documento 64 §3.2). */
  get installmentsConEstado(): { order: number; scheduled_date: string | null; amount: number; estadoTexto: string; estadoClase: string }[] {
    const items = Array.isArray(this.plan?.installments) ? [...this.plan.installments] : [];
    items.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    let yaHuboProxima = false;
    return items.map((c: any) => {
      const fecha = c?.scheduled_date ? new Date(c.scheduled_date) : null;
      let estadoTexto = 'Pendiente';
      let estadoClase = 'kb-badge-neutro';
      if (fecha && !isNaN(fecha.getTime())) {
        const dias = Math.round((fecha.getTime() - hoy.getTime()) / 86400000);
        if (dias < 0) {
          estadoTexto = `Vencida hace ${Math.abs(dias)} día${Math.abs(dias) === 1 ? '' : 's'}`;
          estadoClase = 'kb-badge-peligro';
        } else if (!yaHuboProxima) {
          estadoTexto = dias === 0 ? 'Próxima · vence hoy' : `Próxima · faltan ${dias} día${dias === 1 ? '' : 's'}`;
          estadoClase = 'kb-badge-alerta';
          yaHuboProxima = true;
        }
      }
      return { order: c?.order, scheduled_date: c?.scheduled_date || null, amount: c?.amount, estadoTexto, estadoClase };
    });
  }

  get planResumen(): string {
    const p = this.plan;
    if (!p) return '';
    const cuotas = Array.isArray(p.installments) ? p.installments.length : (p.payment_term ?? 0);
    // 🔴 2026-09-25: decía «3 cuotas de $1,00» leyendo `fee_amount` —que NO es la cuota— con la
    // tabla de abajo en 1,67 · 1,67 · 1,66. La cuota sale de las cuotas mismas.
    const montos = (Array.isArray(p.installments) ? p.installments : [])
      .map((c: any) => Number(c?.amount ?? c?.fee_amount ?? c?.monto)).filter((n: number) => n > 0);
    const cuota = montos.length ? montos[0]
      : (cuotas && p.financed_amount ? Math.round((p.financed_amount / cuotas) * 100) / 100 : null);
    return `Plan ${p.code || '—'} · ${cuotas} cuota${cuotas === 1 ? '' : 's'}${cuota ? ` de ${this.formatoMonto(cuota)}` : ''}`
      + ` · financiado ${this.formatoMonto(p.financed_amount)} sobre ${this.formatoMonto(this.montoUsd)}`;
  }

  // ══ 4 · CRÉDITO — el que nació, o por qué no (documento 63 §4.2 bloque 4) ═══

  /** El estado real, leído de `invoices` — no una copia de la solicitud
   *  (`infra/repos/solicitudes.js:estadoDelCredito`). */
  get creditoReal(): any { return this.solicitud?.credito_real || null; }

  /** Tres campos CURADOS, no la grilla `Object.keys()` de antes (documento 63 §4.2 y §7: «la
   *  grilla generada con Object.keys(), reemplazada por tres campos curados»). */
  get creditoRealActivo(): boolean | null {
    const c = this.creditoReal;
    return c && typeof c.activo === 'boolean' ? c.activo : null;
  }

  get creditoRealEstadoPago(): { code: string; name: string } | null {
    return this.creditoReal?.payment_status || null;
  }
}
